import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { useParams, Link } from 'react-router-dom'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import Button from 'components/button'
import Spinner from 'components/spinner'
import { ADMIN_CONVERSATIONS } from 'routes'
import { ConversationDetail as ConversationDetailType, SKILL_LABELS } from './types'

const formatConversationForClipboard = (detail: ConversationDetailType): string => {
  const header = `Conversation — ${detail.userEmail || detail.userName || '-'}\n${new Date(detail.createdAt).toLocaleString()}\n`
  const messages = detail.messages.map((msg) => {
    const role = msg.role === 'user' ? 'User' : 'Agent'
    const timestamp = new Date(msg.createdAt).toLocaleString()
    const parts = [`${role} - ${timestamp}`]
    if (msg.thinking) parts.push(`Thinking: ${msg.thinking}`)
    if (msg.tools?.length) {
      const toolLines = msg.tools.map((tool) => {
        const label = SKILL_LABELS[tool.skillId] || tool.skillId
        const args = tool.arguments ? `\n  Args: ${JSON.stringify(tool.arguments)}` : ''
        const output = tool.output ? `\n  Output: ${JSON.stringify(tool.output)}` : ''
        return `  [${label}]${args}${output}`
      })
      parts.push(`Tools:\n${toolLines.join('\n')}`)
    }
    parts.push(msg.content)
    if (msg.vote != null) parts.push(msg.vote === 1 ? '\u{1F44D}' : '\u{1F44E}')
    if (msg.feedback) parts.push(`Feedback: ${msg.feedback}`)
    return parts.join('\n')
  })
  return `${header}\n${messages.join('\n\n')}`
}

const AdminConversationDetail: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>()
  const { displayErrorMessage } = useApplicationStore()
  const [detail, setDetail] = useState<ConversationDetailType | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  const fetchConversation = async (id: string) => {
    setLoading(true)
    setDetail(null)
    try {
      const response = await api.getAdminConversation(id)
      setDetail(response.data)
      setLoading(false)
    } catch (e) {
      displayErrorMessage(e)
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Morta | Conversation Detail'
    if (conversationId) {
      fetchConversation(conversationId)
    }
  }, [conversationId])

  if (loading) {
    return (
      <div className="p-5">
        <Spinner />
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="p-5">
        <div className="text-sm text-muted">Conversation not found.</div>
        <Link to={ADMIN_CONVERSATIONS} className="text-blue text-sm underline mt-2 inline-block">
          Back to conversations
        </Link>
      </div>
    )
  }

  return (
    <div className="p-5 w-full">
      <div className="flex items-center gap-2 mb-4">
        <Link to={ADMIN_CONVERSATIONS}>
          <Button internalType="outline" className="text-xs">
            Back
          </Button>
        </Link>
        <Button
          internalType="outline"
          className="text-xs relative"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(formatConversationForClipboard(detail))
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            } catch {
              // Clipboard API not available
            }
          }}
        >
          <span className={copied ? 'invisible' : ''}>Copy Conversation</span>
          {copied && <span className="absolute">Copied!</span>}
        </Button>
        <h2 className="text-lg font-semibold m-0">Conversation — {detail.userEmail || detail.userName || '-'}</h2>
        <span className="text-xs text-muted">{new Date(detail.createdAt).toLocaleString()}</span>
      </div>

      {(detail.hubContextId || detail.viewContextId || detail.documentContextId) && (
        <div className="flex gap-1.5 flex-wrap mb-3">
          {detail.hubContextId && (
            <span className="text-xs py-px px-2 bg-really-light-blue text-blue rounded-[10px]">
              Hub: {detail.hubContextId}
            </span>
          )}
          {detail.viewContextId && (
            <span className="text-xs py-px px-2 bg-really-light-green text-green rounded-[10px]">
              View: {detail.viewContextId}
            </span>
          )}
          {detail.documentContextId && (
            <span className="text-xs py-px px-2 bg-really-light-yellow text-muted rounded-[10px]">
              Document: {detail.documentContextId}
            </span>
          )}
        </div>
      )}

      <div className="max-w-[900px]">
        {detail.messages.map((msg) => {
          const isUser = msg.role === 'user'
          return (
            <div className="mb-3" key={msg.publicId}>
              <div className="text-xs text-muted mb-1">
                <strong>{isUser ? 'User' : 'Agent'}</strong>
                {' - '}
                {new Date(msg.createdAt).toLocaleString()}
              </div>
              {!isUser && msg.thinking && (
                <div className="text-xs text-muted whitespace-pre-wrap mb-1 bg-light-purple py-1.5 px-2 rounded">
                  <strong>Thinking:</strong> {msg.thinking}
                </div>
              )}
              {!isUser && msg.tools && msg.tools.length > 0 && (
                <div className="flex flex-col gap-[1px] mb-1">
                  <span className="text-xs text-muted">Tools:</span>
                  {msg.tools.map((tool) => (
                    <div key={tool.publicId} className="flex flex-col gap-[1px]">
                      <div className="flex items-center gap-[1px] flex-wrap">
                        <span className="text-xs font-medium py-px px-2 bg-really-light-blue text-blue rounded-[10px]">
                          {SKILL_LABELS[tool.skillId] || tool.skillId}
                        </span>
                        {tool.implemented === true ? (
                          <span className="text-xs font-medium py-px px-2 bg-really-light-green text-green rounded-[10px]">
                            Yes
                          </span>
                        ) : tool.implemented === false ? (
                          <span className="text-xs font-medium py-px px-2 bg-really-light-red text-red rounded-[10px]">
                            No
                          </span>
                        ) : null}
                        {tool.error?.message && <span className="text-xs text-red">{tool.error.message}</span>}
                      </div>
                      {tool.arguments && (
                        <pre className="text-[11px] text-muted bg-surface-muted rounded px-2 py-1 overflow-x-auto max-w-full">
                          {JSON.stringify(tool.arguments, null, 2)}
                        </pre>
                      )}
                      {tool.output && (
                        <pre className="text-[11px] text-muted bg-light-green rounded px-2 py-1 overflow-x-auto max-w-full">
                          <strong>Output:</strong> {JSON.stringify(tool.output, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div
                className={`text-sm py-2 px-3 rounded-lg ${isUser ? 'bg-really-light-blue whitespace-pre-wrap' : 'chat-markdown bg-surface-muted'}`}
              >
                {isUser ? msg.content : <ReactMarkdown disallowedElements={['img']}>{msg.content}</ReactMarkdown>}
              </div>
              {(msg.vote != null || msg.feedback) && (
                <div className="text-xs text-muted mt-1 py-1 px-2 bg-really-light-yellow rounded">
                  {msg.vote != null && <span className="mr-1.5">{msg.vote === 1 ? '\u{1F44D}' : '\u{1F44E}'}</span>}
                  {msg.feedback && <>Feedback: {msg.feedback}</>}
                </div>
              )}
            </div>
          )
        })}
        {detail.messages.length === 0 && <div className="text-sm text-muted">No messages in this conversation.</div>}
      </div>
    </div>
  )
}

export default AdminConversationDetail
